/*
 * Ballr — automatic placeholder removal, in the browser.
 *
 * Ballr's artwork only exists flattened, with the sample NAME / 00 printed into
 * it, so a preview needs a copy of the poster without them. Rather than have
 * someone prepare that by hand for every new product, this rebuilds it on the
 * fly: load the photo into a canvas, work out what the kit does behind the
 * lettering, and paint only the lettering out.
 *
 * Method (the same one arrived at offline, after simpler ones failed):
 *   - one merged block per shirt, so the reference rows are real kit and not
 *     the neighbouring glyph;
 *   - fit a shallow per-column curve of the kit from the clean rows above and
 *     below, which follows both the stripe colour of each column and the
 *     shading down the shirt;
 *   - mask whatever departs from that curve — fill, outline and shadow alike,
 *     whatever colour they happen to be — and replace only those pixels;
 *   - add the kit's fine grain back from the neighbouring rows, clamped, or the
 *     patch reads flatter than the fabric around it.
 *
 * A product that supplies its own clean artwork skips all of this.
 */
(function () {
  'use strict';

  var MAX_W = 1400;   // plenty for a product photo, and keeps the pass quick

  function solveLstsq(A, B, n, deg) {
    // normal equations: (A'A) x = A'B, tiny system (deg+1 square)
    var m = deg + 1, i, j, k;
    var AtA = [], AtB = [];
    for (i = 0; i < m; i++) {
      AtA.push(new Float64Array(m));
      AtB.push(new Float64Array(B[0].length));
    }
    for (i = 0; i < m; i++) {
      for (j = 0; j < m; j++) {
        var s = 0;
        for (k = 0; k < n; k++) s += A[k][i] * A[k][j];
        AtA[i][j] = s;
      }
      for (var c = 0; c < B[0].length; c++) {
        var t = 0;
        for (k = 0; k < n; k++) t += A[k][i] * B[k][c];
        AtB[i][c] = t;
      }
    }
    // Gaussian elimination with partial pivoting
    for (i = 0; i < m; i++) {
      var piv = i;
      for (j = i + 1; j < m; j++) if (Math.abs(AtA[j][i]) > Math.abs(AtA[piv][i])) piv = j;
      var tr = AtA[i]; AtA[i] = AtA[piv]; AtA[piv] = tr;
      var tb = AtB[i]; AtB[i] = AtB[piv]; AtB[piv] = tb;
      if (Math.abs(AtA[i][i]) < 1e-9) return null;
      for (j = i + 1; j < m; j++) {
        var f = AtA[j][i] / AtA[i][i];
        for (k = i; k < m; k++) AtA[j][k] -= f * AtA[i][k];
        for (k = 0; k < AtB[0].length; k++) AtB[j][k] -= f * AtB[i][k];
      }
    }
    var X = [];
    for (i = 0; i < m; i++) X.push(new Float64Array(AtB[0].length));
    for (i = m - 1; i >= 0; i--) {
      for (k = 0; k < AtB[0].length; k++) {
        var v = AtB[i][k];
        for (j = i + 1; j < m; j++) v -= AtA[i][j] * X[j][k];
        X[i][k] = v / AtA[i][i];
      }
    }
    return X;
  }

  function eraseBlock(px, W, H, rect, opts) {
    var x0 = Math.max(0, Math.round(rect[0] * W));
    var x1 = Math.min(W, Math.round(rect[2] * W));
    var y0 = Math.max(0, Math.round(rect[1] * H));
    var y1 = Math.min(H, Math.round(rect[3] * H));
    var n = y1 - y0, w = x1 - x0;
    if (n < 6 || w < 6) return;

    var deg = opts.deg, thresh = opts.thresh, fillAll = opts.fillAll;
    var pad = Math.max(8, Math.round(n * opts.ctx));
    var ca = Math.max(0, y0 - pad), cb = Math.min(H, y1 + pad);

    var rows = [];
    for (var y = ca; y < y0; y++) rows.push(y);
    for (y = y1; y < cb; y++) rows.push(y);
    if (rows.length < 2 * (deg + 1)) return;

    var mid = (y0 + y1) / 2, scale = Math.max(1, n);
    var A = [], B = [];
    for (var r = 0; r < rows.length; r++) {
      var t = (rows[r] - mid) / scale, row = [];
      for (var d = deg; d >= 0; d--) row.push(Math.pow(t, d));
      A.push(row);
      var vals = new Float64Array(w * 3);
      for (var x = 0; x < w; x++) {
        var o = (rows[r] * W + x0 + x) * 4;
        vals[x * 3] = px[o]; vals[x * 3 + 1] = px[o + 1]; vals[x * 3 + 2] = px[o + 2];
      }
      B.push(vals);
    }
    var coef = solveLstsq(A, B, rows.length, deg);
    if (!coef) return;

    function predict(y) {
      var t = (y - mid) / scale, out = new Float64Array(w * 3), d, p;
      for (d = 0; d <= deg; d++) {
        p = Math.pow(t, deg - d);
        var cf = coef[d];
        for (var i = 0; i < w * 3; i++) out[i] += cf[i] * p;
      }
      return out;
    }

    // fine grain from just outside the block, clamped so structural
    // differences (shoulder stripes) cannot bleed inward
    var LIM = 18, band = Math.max(2, Math.min(6, y0 - ca, cb - y1));
    function residual(rowsList) {
      var acc = new Float64Array(w * 3), c;
      for (var i = 0; i < rowsList.length; i++) {
        var yy = rowsList[i], pr = predict(yy);
        for (var j = 0; j < w; j++) {
          var o = (yy * W + x0 + j) * 4;
          acc[j * 3] += px[o] - pr[j * 3];
          acc[j * 3 + 1] += px[o + 1] - pr[j * 3 + 1];
          acc[j * 3 + 2] += px[o + 2] - pr[j * 3 + 2];
        }
      }
      for (c = 0; c < acc.length; c++) {
        acc[c] /= rowsList.length;
        acc[c] = Math.max(-LIM, Math.min(LIM, acc[c]));
      }
      return acc;
    }
    var top = [], bot = [];
    for (var i2 = 1; i2 <= band; i2++) { if (y0 - i2 >= ca) top.push(y0 - i2); }
    for (i2 = 0; i2 < band; i2++) { if (y1 + i2 < cb) bot.push(y1 + i2); }
    var rTop = top.length ? residual(top) : new Float64Array(w * 3);
    var rBot = bot.length ? residual(bot) : rTop;

    var mask = new Uint8Array(n * w);
    var preds = [];
    for (y = y0; y < y1; y++) {
      var pr2 = predict(y);
      preds.push(pr2);
      if (fillAll) continue;
      for (x = 0; x < w; x++) {
        var o2 = (y * W + x0 + x) * 4;
        var dd = Math.abs(px[o2] - pr2[x * 3]) + Math.abs(px[o2 + 1] - pr2[x * 3 + 1]) +
                 Math.abs(px[o2 + 2] - pr2[x * 3 + 2]);
        if (dd > thresh) mask[(y - y0) * w + x] = 1;
      }
    }
    if (!fillAll) {
      for (var pass = 0; pass < 2; pass++) {          // dilate, to catch soft edges
        var copy = mask.slice();
        for (y = 0; y < n; y++) for (x = 0; x < w; x++) {
          if (copy[y * w + x]) continue;
          if ((y > 0 && copy[(y - 1) * w + x]) || (y < n - 1 && copy[(y + 1) * w + x]) ||
              (x > 0 && copy[y * w + x - 1]) || (x < w - 1 && copy[y * w + x + 1])) mask[y * w + x] = 1;
        }
      }
    }

    for (y = y0; y < y1; y++) {
      var k2 = (y - y0), pr3 = preds[k2], tt = n > 1 ? k2 / (n - 1) : 0;
      for (x = 0; x < w; x++) {
        if (!fillAll && !mask[k2 * w + x]) continue;
        var o3 = (y * W + x0 + x) * 4, b3 = x * 3;
        for (var ch = 0; ch < 3; ch++) {
          var tex = rTop[b3 + ch] * (1 - tt) + rBot[b3 + ch] * tt;
          var val = pr3[b3 + ch] + (fillAll ? 0 : tex);
          px[o3 + ch] = val < 0 ? 0 : (val > 255 ? 255 : val);
        }
      }
    }
  }

  /* Poster-space rect -> image-space rect. These mockups are shot square-on,
     so the quad is near axis-aligned and its bounding box is accurate. */
  function toImageRect(quad, rect) {
    function map(u, v) {
      var top = [quad[0][0] + (quad[1][0] - quad[0][0]) * u, quad[0][1] + (quad[1][1] - quad[0][1]) * u];
      var bot = [quad[3][0] + (quad[2][0] - quad[3][0]) * u, quad[3][1] + (quad[2][1] - quad[3][1]) * u];
      return [top[0] + (bot[0] - top[0]) * v, top[1] + (bot[1] - top[1]) * v];
    }
    var pts = [map(rect[0], rect[1]), map(rect[2], rect[1]), map(rect[2], rect[3]), map(rect[0], rect[3])];
    var xs = pts.map(function (p) { return p[0]; });
    var ys = pts.map(function (p) { return p[1]; });
    return [Math.min.apply(null, xs), Math.min.apply(null, ys),
            Math.max.apply(null, xs), Math.max.apply(null, ys)];
  }

  var PARAMS = {
    fullbleed: { thresh: 105, ctx: 0.30, deg: 1 },
    locker2:   { thresh: 90,  ctx: 0.25, deg: 1 },
    card1:     { thresh: 90,  ctx: 0.25, deg: 1 },
    couples:   { thresh: 58,  ctx: 0.25, deg: 2 }
  };


  /* ---------------------------------------------------------------
   * Full-bleed posters put the name and number in a different place
   * on every club's design, so one fixed geometry cannot serve them.
   * Find the printed placeholder instead: it is a flat colour forming
   * two centred bands — a tall number with a shorter name above it.
   * --------------------------------------------------------------- */
  function bandsOf(mask, W, y0, y1, minH, thrFrac) {
    var out = [], cur = null, thr = Math.max(2, (W * thrFrac) | 0);
    for (var y = y0; y < y1; y++) {
      var c = 0;
      for (var x = 0; x < W; x++) if (mask[y * W + x]) c++;
      if (c > thr) { if (!cur) cur = [y, y]; else cur[1] = y; }
      else { if (cur && cur[1] - cur[0] >= minH) out.push(cur); cur = null; }
    }
    if (cur && cur[1] - cur[0] >= minH) out.push(cur);
    return out;
  }

  function detectPlaceholder(px, W, H, quad) {
    var px0 = Math.round(quad[0][0] * W), px1 = Math.round(quad[1][0] * W);
    var py0 = Math.round(quad[0][1] * H), py1 = Math.round(quad[2][1] * H);
    var pw = px1 - px0, ph = py1 - py0;
    if (pw < 40 || ph < 40) return null;

    var sx0 = px0 + ((pw * 0.10) | 0), sx1 = px0 + ((pw * 0.90) | 0);
    var sy0 = py0 + ((ph * 0.08) | 0), sy1 = py0 + ((ph * 0.80) | 0);

    var counts = {}, step = 2;
    for (var y = sy0; y < sy1; y += step) {
      for (var x = sx0; x < sx1; x += step) {
        var o = (y * W + x) * 4;
        var k = ((px[o] >> 5) << 10) | ((px[o + 1] >> 5) << 5) | (px[o + 2] >> 5);
        counts[k] = (counts[k] || 0) + 1;
      }
    }
    var keys = Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; }).slice(0, 12);

    var best = null, mask = new Uint8Array(W * H);
    for (var ki = 0; ki < keys.length; ki++) {
      var k2 = parseInt(keys[ki], 10);
      var cr = ((k2 >> 10) & 31) * 8 + 4, cg = ((k2 >> 5) & 31) * 8 + 4, cb = (k2 & 31) * 8 + 4;
      mask.fill(0);
      var n = 0;
      for (y = sy0; y < sy1; y++) for (x = sx0; x < sx1; x++) {
        var o2 = (y * W + x) * 4;
        if (Math.abs(px[o2] - cr) + Math.abs(px[o2 + 1] - cg) + Math.abs(px[o2 + 2] - cb) < 90) { mask[y * W + x] = 1; n++; }
      }
      if (n < pw * ph * 0.004) continue;

      var bs = bandsOf(mask, W, sy0, sy1, Math.max(3, (ph * 0.018) | 0), 0.010);
      if (bs.length < 2) continue;
      var num = bs.reduce(function (a, b) { return (b[1] - b[0]) > (a[1] - a[0]) ? b : a; });
      var nh = (num[1] - num[0]) / ph;
      if (nh < 0.13 || nh > 0.46) continue;

      function extent(band) {
        var lo = 1e9, hi = -1;
        for (var yy = band[0]; yy <= band[1]; yy++)
          for (var xx = sx0; xx < sx1; xx++)
            if (mask[yy * W + xx]) { if (xx < lo) lo = xx; if (xx > hi) hi = xx; }
        return [lo, hi];
      }
      var ne = extent(num);
      var ncx = ((ne[0] + ne[1]) / 2 - px0) / pw;
      if (Math.abs(ncx - 0.5) > 0.13) continue;

      var above = bs.filter(function (b) { return b[1] < num[0] - 2; });
      if (!above.length) continue;
      var nam = above.reduce(function (a, b) { return (b[1] - b[0]) > (a[1] - a[0]) ? b : a; });
      var mh = (nam[1] - nam[0]) / ph;
      if (mh < 0.025 || mh > 0.17) continue;
      var me = extent(nam);
      var mcx = ((me[0] + me[1]) / 2 - px0) / pw;
      if (Math.abs(mcx - 0.5) > 0.15) continue;

      var score = nh * (ne[1] - ne[0]) / pw;
      if (!best || score > best.score) {
        best = {
          score: score,
          colour: [cr, cg, cb],
          name:   { cx: mcx, cy: ((nam[0] + nam[1]) / 2 - py0) / ph, h: mh,
                    w: (me[1] - me[0]) / pw },
          number: { cx: ncx, cy: ((num[0] + num[1]) / 2 - py0) / ph, h: nh,
                    w: (ne[1] - ne[0]) / pw }
        };
      }
    }
    return best;
  }

  window.BallrDetectPlaceholder = function (srcUrl, quad) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onerror = function () { resolve(null); };
      img.onload = function () {
        try {
          var scale = Math.min(1, 900 / img.naturalWidth);
          var W = Math.round(img.naturalWidth * scale), H = Math.round(img.naturalHeight * scale);
          var cv = document.createElement('canvas');
          cv.width = W; cv.height = H;
          var ctx = cv.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, W, H);
          resolve(detectPlaceholder(ctx.getImageData(0, 0, W, H).data, W, H, quad));
        } catch (e) { resolve(null); }
      };
      img.src = srcUrl;
    });
  };

  window.BallrAutoErase = function (srcUrl, layout, family) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onerror = function () { reject(new Error('image load failed')); };
      img.onload = function () {
        try {
          var scale = Math.min(1, MAX_W / img.naturalWidth);
          var W = Math.round(img.naturalWidth * scale);
          var H = Math.round(img.naturalHeight * scale);
          var cv = document.createElement('canvas');
          cv.width = W; cv.height = H;
          var ctx = cv.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, W, H);
          var data = ctx.getImageData(0, 0, W, H);
          var opts = PARAMS[family] || PARAMS.fullbleed;
          (layout.erase || []).forEach(function (r) {
            var o = { thresh: opts.thresh, ctx: opts.ctx, deg: opts.deg, fillAll: r[4] === 'fill' };
            eraseBlock(data.data, W, H, toImageRect(layout.quad, r), o);
          });
          ctx.putImageData(data, 0, 0);
          resolve(cv.toDataURL('image/jpeg', 0.92));
        } catch (e) {
          reject(e);
        }
      };
      img.src = srcUrl;
    });
  };
})();
